import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { products } from "@/mocks/products";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);

  const [activeCategory, setActiveCategory] = useState(slug || "children");

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const filteredProducts = products.filter((p) => p.category === activeCategory);
  const activeCat = categories.find((c) => c.slug === activeCategory);

  return (
    <div className="min-h-screen pt-20 pb-14 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="py-4">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: "产品分类", path: "/category/children" },
              { label: activeCat?.name || "" },
            ]}
          />
        </div>

        <div className="py-8 md:py-12">
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
              {activeCat?.name || "产品分类"}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              {activeCat?.description || ""}
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm rounded-full whitespace-nowrap transition-all duration-200 no-underline hover:no-underline ${
                  activeCategory === cat.slug
                    ? "bg-primary-500 text-background-50 font-medium"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200/70"
                }`}
              >
                <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无产品，请浏览其他分类</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
              {filteredProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/article/${product.slug}`}
                  className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden hover:border-background-300 transition-all duration-300 no-underline hover:no-underline"
                >
                  <div className="relative aspect-square overflow-hidden bg-background-100">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    {product.tags.length > 0 && (
                      <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                        {product.tags.map((tag) => (
                          <span key={tag} className="px-2 py-0.5 text-[11px] font-medium rounded-md bg-accent-500 text-background-50">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-foreground-500 mb-1">{product.brand}</p>
                    <h3 className="text-sm font-medium text-foreground-900 mb-2 line-clamp-2 leading-snug">
                      {product.name}
                    </h3>
                    <div className="flex items-center gap-1.5 mb-2">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i
                            key={i}
                            className={`text-[10px] ${
                              i < Math.floor(product.rating)
                                ? "ri-star-fill text-accent-500"
                                : "ri-star-line text-foreground-300"
                            }`}
                          ></i>
                        ))}
                      </div>
                      <span className="text-xs text-foreground-600">{product.rating}</span>
                      <span className="text-xs text-foreground-400">({product.reviewCount}评)</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-primary-500">¥{product.price}</span>
                      <span className="text-[11px] text-foreground-500 bg-background-50 px-2 py-0.5 rounded-md">
                        {product.calciumContent}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}